import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { nafToSegment } from '@shared/constants/naf-to-segment';

/* ────────────────────────────────────────────────────────────
   Types métier
──────────────────────────────────────────────────────────── */

/**
 * Étape du funnel — utilisée par le composant DiagnosticFunnel pour piloter
 * l'écran courant. Le store NE PILOTE PLUS la step (c'est un state local
 * `localStep` dans le composant) pour éviter le conflit de source de vérité ;
 * ce type est exposé ici pour le typage partagé uniquement.
 */
export type DiagnosticStep =
  | 'profile'
  | 'siret'
  | 'questions'
  | 'lead'
  | 'estimation'
  | 'done';

/** Question dynamique envoyée par `/api/diagnostic/siret` (5 questions persona-aware). */
export interface DiagnosticQuestion {
  id: string;
  type: 'multi-select' | 'select' | 'number' | 'text';
  label: string;
  options?: { id: string; label: string }[];
  min?: number;
}

/** Réponse utilisateur à une question (typage volontairement large). */
export interface DiagnosticAnswer {
  questionId: string;
  value: string | number | string[];
}

/** Coordonnées saisies à l'étape lead — persistées pour survivre au F5. */
export interface DiagnosticContact {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  consentMarketing: boolean;
}

/** Résultat retourné par `/api/diagnostic/estimer`. */
export interface DiagnosticEstimation {
  primeMin: number;
  primeMax: number;
  fichesEligibles: {
    code: string;
    label: string;
    primeMin: number;
    primeMax: number;
    /** computed = calcul moteur ATEE avec la surface · range = fourchette · study = sur étude */
    estimationType?: 'computed' | 'range' | 'study';
  }[];
  travauxRecommandes: string[];
}

/* ────────────────────────────────────────────────────────────
   Store Zustand
──────────────────────────────────────────────────────────── */

interface DiagnosticState {
  // ── Contexte entreprise ──
  siret: string;
  companyName: string;
  naf: string | null;
  segment: string;
  isAgri: boolean;
  /** Profil sélectionné à l'étape 1 (artisan | professionnel | entreprise). */
  profileId: string | null;

  // ── Questions / réponses ──
  questions: DiagnosticQuestion[];
  answers: DiagnosticAnswer[];

  // ── Résultat ──
  estimation: DiagnosticEstimation | null;

  // ── Lead ──
  leadId: string | null;
  /** Champs du formulaire lead (persistés — évite la perte de saisie au F5). */
  contact: DiagnosticContact;

  // ── UI ──
  loading: boolean;
  error: string | null;

  // ── Actions ──
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  /** Pose le contexte entreprise après l'appel `/api/diagnostic/siret`. */
  setSiretContext: (ctx: {
    siret: string;
    name: string;
    naf: string | null;
    segment: string;
    questions: DiagnosticQuestion[];
  }) => void;

  /** Insère ou met à jour la réponse pour une question. */
  setAnswer: (answer: DiagnosticAnswer) => void;

  /** Stocke le résultat de l'estimation. La transition d'étape est gérée par le composant. */
  setEstimation: (estimation: DiagnosticEstimation) => void;

  /** Stocke l'ID du lead. La transition vers l'étape "done" est gérée par le composant. */
  setLead: (leadId: string) => void;
  /** Met à jour partiellement les champs du formulaire lead. */
  patchContact: (patch: Partial<DiagnosticContact>) => void;
  /** Stocke le profil sélectionné à l'étape 1. */
  setProfileId: (profileId: string) => void;

  /** Détecte le segment depuis le NAF (utilitaire). */
  detectSegment: (naf: string) => void;

  /** Remet tout à zéro. */
  reset: () => void;
}

const INITIAL_CONTACT: DiagnosticContact = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  consentMarketing: false,
};

const INITIAL: Omit<
  DiagnosticState,
  | 'setLoading'
  | 'setError'
  | 'setSiretContext'
  | 'setAnswer'
  | 'setEstimation'
  | 'setLead'
  | 'setProfileId'
  | 'patchContact'
  | 'detectSegment'
  | 'reset'
> = {
  siret: '',
  companyName: '',
  naf: null,
  segment: '',
  isAgri: false,
  profileId: null,
  questions: [],
  answers: [],
  estimation: null,
  leadId: null,
  contact: INITIAL_CONTACT,
  loading: false,
  error: null,
};

export const useDiagnosticStore = create<DiagnosticState>()(
  persist(
    (set) => ({
      ...INITIAL,

      setLoading: (loading) => set({ loading }),

      setError: (error) => set({ error }),

      setSiretContext: ({ siret, name, naf, segment, questions }) =>
        set({
          siret,
          companyName: name,
          naf,
          segment,
          isAgri: segment === 'agri',
          questions,
        }),

      setAnswer: (answer) =>
        set((state) => {
          const idx = state.answers.findIndex((a) => a.questionId === answer.questionId);
          const next =
            idx === -1
              ? [...state.answers, answer]
              : state.answers.map((a, i) => (i === idx ? answer : a));
          return { answers: next };
        }),

      setEstimation: (estimation) => set({ estimation }),

      setLead: (leadId) => set({ leadId }),

      patchContact: (patch) =>
        set((state) => ({ contact: { ...state.contact, ...patch } })),

      setProfileId: (profileId) => set({ profileId }),

      detectSegment: (naf) => {
        const segment = nafToSegment(naf);
        set({ naf, segment, isAgri: segment === 'agri' });
      },

      reset: () => set({ ...INITIAL }),
    }),
    {
      name: 'sef_diagnostic',
      storage: createJSONStorage(() => localStorage),
      version: 2,
      // On ne persiste PAS l'état transitoire (loading/error) — il doit être
      // remis à plat à chaque rechargement. Tout le reste reste persisté
      // pour permettre de reprendre le funnel sur F5.
      partialize: (state) => ({
        siret: state.siret,
        companyName: state.companyName,
        naf: state.naf,
        segment: state.segment,
        isAgri: state.isAgri,
        profileId: state.profileId,
        questions: state.questions,
        answers: state.answers,
        estimation: state.estimation,
        leadId: state.leadId,
        contact: state.contact,
      }),
      // Bump v1 → v2 : l'ancien schéma était {currentStep: 1-5, data: {...}},
      // complètement incompatible avec la nouvelle API du funnel. On reset si
      // l'ancien shape est détecté pour éviter tout crash au runtime.
      migrate: (persistedState, version) => {
        if (version < 2) return INITIAL;
        return persistedState as DiagnosticState;
      },
    },
  ),
);

/** Alias rétro-compat avec l'ancien nom d'import. */
export const useDiagnostic = useDiagnosticStore;
